import { nowUtc } from '@/lib/clock';
import { newUuid } from '@/lib/ids';
import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import type { AttachmentMeta, BackupScope } from '@/data/db';
import { newAudit } from '@/data/repositories/support';
import {
  BACKUP_FORMAT,
  BACKUP_VERSION,
  EXPORT_MATRIX,
  type BackupFile,
  type ExportKey,
} from './backup-schema';
import { attachmentFileName, packBackupZip, type ZipEntry } from './zip';
import { isDesktop } from '@/platform';
import {
  buildBackupDatabase,
  readNoteImageFile,
  waitForNoteImageOperations,
} from '@/platform/note-image-files';
import { toChange } from '@/data/mirror/mirror-payload';
import { sha256Hex } from '@/features/attachments/process-image';

/**
 * 版本號由 Vite 從 package.json 注入（desktop-app.md 5.4）。
 * 測試與 Node 腳本沒有 define，退回讀不到時的預設值。
 */
export const APP_VERSION: string =
  typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0-dev';

function includes(key: ExportKey, scope: BackupScope): boolean {
  return (EXPORT_MATRIX[key] as readonly BackupScope[]).includes(scope);
}

/**
 * 完整備份包含垃圾桶內容；分項匯出只含未刪除的資料（data-model 第 10 節）。
 */
function keepForScope<T extends { deletedAt?: string | null }>(rows: T[], scope: BackupScope): T[] {
  if (scope === 'full') return rows;
  return rows.filter((row) => row.deletedAt == null);
}

export async function buildBackup(
  scope: BackupScope,
  db: GentleTaskDatabase = defaultDb,
): Promise<BackupFile> {
  const backup: BackupFile = {
    format: BACKUP_FORMAT,
    version: BACKUP_VERSION,
    scope,
    exportedAt: nowUtc(),
    appVersion: APP_VERSION,
    counts: {},
  };

  if (includes('tasks', scope)) {
    backup.tasks = keepForScope(await db.tasks.toArray(), scope);
  }
  if (includes('taskContacts', scope)) {
    backup.taskContacts = await db.taskContacts.toArray();
  }
  if (includes('recurrenceRules', scope)) {
    backup.recurrenceRules = await db.recurrenceRules.toArray();
  }
  if (includes('contacts', scope)) {
    backup.contacts = keepForScope(await db.contacts.toArray(), scope);
  }
  if (includes('classifications', scope)) {
    backup.classifications = {
      taskTypes: await db.taskTypes.toArray(),
      sourceTypes: await db.sourceTypes.toArray(),
      tags: await db.tags.toArray(),
      contactTypes: await db.contactTypes.toArray(),
      relationships: await db.relationships.toArray(),
    };
  }
  if (includes('followUpLogs', scope)) {
    backup.followUpLogs = keepForScope(await db.followUpLogs.toArray(), scope);
  }
  if (includes('supervisorReminders', scope)) {
    backup.supervisorReminders = await db.supervisorReminders.toArray();
  }
  if (includes('board', scope)) {
    backup.board = {
      columns: await db.boardColumns.toArray(),
      placements: await db.boardPlacements.toArray(),
      matrixPlacements: await db.matrixPlacements.toArray(),
    };
  }
  if (includes('calendarEvents', scope)) {
    backup.calendarEvents = keepForScope(await db.calendarEvents.toArray(), scope);
  }
  if (includes('calendarReminders', scope)) {
    backup.calendarReminders = await db.calendarReminders.toArray();
  }
  if (includes('notifications', scope)) {
    backup.notifications = await db.notifications.toArray();
  }
  if (includes('attachments', scope)) {
    // data.json 只放 metadata；原圖走 ZIP 的 images/（第一版尚未實作圖片）。
    backup.attachments = (await db.attachments.toArray()).map<AttachmentMeta>((attachment) => ({
      id: attachment.id,
      ownerType: attachment.ownerType,
      ownerId: attachment.ownerId,
      fileName: `images/${attachment.id}.${extensionFor(attachment.mimeType)}`,
      mimeType: attachment.mimeType,
      originalSize: attachment.originalSize,
      size: attachment.size,
      width: attachment.width,
      height: attachment.height,
      sha256: attachment.sha256,
      createdAt: attachment.createdAt,
    }));
  }
  if (includes('notes', scope)) {
    backup.notes = keepForScope(await db.notes.toArray(), scope);
  }
  if (includes('noteImages', scope)) backup.noteImages = await db.noteImages.toArray();
  if (includes('settings', scope)) {
    const settings = await db.settings.get('singleton');
    if (settings) backup.settings = settings;
  }
  if (includes('backupHistory', scope)) {
    backup.backupHistory = await db.backupHistory.toArray();
  }

  backup.counts = countCollections(backup);
  return backup;
}

function extensionFor(mimeType: string): string {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/jpeg') return 'jpg';
  return 'webp';
}

export function countCollections(backup: BackupFile): Record<string, number> {
  const counts: Record<string, number> = {};
  const add = (key: string, value: number | undefined) => {
    if (value !== undefined) counts[key] = value;
  };

  add('tasks', backup.tasks?.length);
  add('taskContacts', backup.taskContacts?.length);
  add('recurrenceRules', backup.recurrenceRules?.length);
  add('contacts', backup.contacts?.length);
  add('followUpLogs', backup.followUpLogs?.length);
  add('supervisorReminders', backup.supervisorReminders?.length);
  add('calendarEvents', backup.calendarEvents?.length);
  add('calendarReminders', backup.calendarReminders?.length);
  add('notifications', backup.notifications?.length);
  add('attachments', backup.attachments?.length);
  add('backupHistory', backup.backupHistory?.length);
  add('notes', backup.notes?.length);
  add('noteImages', backup.noteImages?.length);

  if (backup.classifications) {
    add('taskTypes', backup.classifications.taskTypes.length);
    add('sourceTypes', backup.classifications.sourceTypes.length);
    add('tags', backup.classifications.tags.length);
    add('contactTypes', backup.classifications.contactTypes.length);
    add('relationships', backup.classifications.relationships.length);
  }
  if (backup.board) {
    add('boardColumns', backup.board.columns.length);
    add('boardPlacements', backup.board.placements.length);
    add('matrixPlacements', backup.board.matrixPlacements.length);
  }
  if (backup.settings) counts['settings'] = 1;

  return counts;
}

export function serializeBackup(backup: BackupFile): string {
  return JSON.stringify(backup, null, 2);
}

export const SCOPE_LABELS: Record<BackupScope, string> = {
  full: '完整備份',
  tasks: '任務清單',
  board: '個人看板',
  calendar: '行事曆',
};

/**
 * 檔名要能一眼看出「哪個網站、什麼時候」（spec 20.1）。
 *
 * 同一天可能匯出很多次，所以時間精確到分；來源網域也寫進去，
 * 因為同一台電腦可能同時有正式站與本機開發站兩份資料。
 */
export function backupStamp(at: Date = new Date()): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `-${pad(at.getHours())}${pad(at.getMinutes())}`
  );
}

/** 網域轉成檔名安全的字串；取不到時用 local。 */
export function backupHostSlug(host?: string): string {
  const raw = host ?? (typeof window === 'undefined' ? '' : window.location.hostname);
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, '')
    .replace(/[.]/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'local';
}

function scopeSlug(scope: BackupScope): string {
  return scope === 'full' ? 'full-backup' : scope;
}

export function backupFileName(scope: BackupScope, at: Date = new Date(), host?: string): string {
  return `gentletask-${backupHostSlug(host)}-${scopeSlug(scope)}-${backupStamp(at)}.json`;
}

/** 匯出前顯示的估算資訊（spec 20.3）。 */
export interface BackupEstimate {
  byteSize: number;
  counts: Record<string, number>;
  attachmentCount: number;
}

export function estimateBackup(backup: BackupFile): BackupEstimate {
  const json = serializeBackup(backup);
  return {
    byteSize: new TextEncoder().encode(json).length,
    counts: backup.counts,
    attachmentCount: (backup.attachments?.length ?? 0) + (backup.noteImages?.length ?? 0),
  };
}

/** 匯出後記錄一筆備份歷史，供備份提醒比較時間。 */
export async function recordBackupHistory(
  scope: BackupScope,
  fileName: string,
  estimate: BackupEstimate,
  db: GentleTaskDatabase = defaultDb,
): Promise<void> {
  const timestamp = nowUtc();
  await db.transaction('rw', [db.backupHistory, db.settings], async () => {
    await db.backupHistory.add({
      id: newUuid(),
      scope,
      exportedAt: timestamp,
      fileName,
      itemCounts: estimate.counts,
      attachmentCount: estimate.attachmentCount,
      byteSize: estimate.byteSize,
      ...newAudit(timestamp),
    });
    // 匯出本身不算資料異動，所以完全不碰 lastDataMutationAt。
    // 之前會在它是 null 時寫成「現在」，讓從沒改過的工作台開始倒數未備份天數。
  });
}

/**
 * 完整備份的 ZIP：data.json 加上 images/ 底下的原圖（spec 20.2）。
 * 逐檔取出，不一次把所有圖片讀進一個大陣列。
 */
export async function packFullBackup(
  db: GentleTaskDatabase = defaultDb,
): Promise<{ archive: Uint8Array; estimate: BackupEstimate }> {
  await waitForNoteImageOperations();
  // Capture all database rows in one read transaction. File/Blob conversion and
  // native IPC happen AFTER it commits, avoiding IndexedDB transaction expiry.
  const { backup, rows } = await db.transaction('r', db.tables, async () => {
    const backup = await buildBackup('full', db);
    const rows = await Promise.all(
      db.tables.map(async (table) => ({
        table: table.name,
        values: (await table.toArray()) as Array<Record<string, unknown>>,
      })),
    );
    return { backup, rows };
  });
  const attachments = rows.find((row) => row.table === 'attachments')?.values ?? [];

  const images: ZipEntry[] = [];
  for (const attachment of attachments) {
    images.push({
      path: attachmentFileName(
        String(attachment['id']),
        attachment['mimeType'] as AttachmentMeta['mimeType'],
      ),
      bytes: new Uint8Array(await (attachment['blob'] as Blob).arrayBuffer()),
    });
  }

  for (const image of backup.noteImages ?? []) {
    const bytes = await readNoteImageFile(image.noteId, image.id);
    if (
      bytes.byteLength !== image.size ||
      (await sha256Hex(new Blob([bytes as BlobPart]))) !== image.sha256
    ) {
      throw new Error(`圖片 ${image.fileName} 與儲存紀錄不符，沒有產生不完整的備份`);
    }
    images.push({ path: image.fileName, bytes });
  }
  if (isDesktop()) {
    const changes = [];
    for (const row of rows) {
      for (const value of row.values)
        changes.push(await toChange(row.table, String(value['id']), value));
    }
    images.push({
      path: 'gentletask.db',
      bytes: await buildBackupDatabase(changes, backup.exportedAt),
    });
  }

  const json = serializeBackup(backup);
  const archive = await packBackupZip(json, images);

  return {
    archive,
    estimate: {
      byteSize: archive.byteLength,
      counts: backup.counts,
      attachmentCount: attachments.length + (backup.noteImages?.length ?? 0),
    },
  };
}

export function backupZipFileName(at: Date = new Date(), host?: string): string {
  return `gentletask-${backupHostSlug(host)}-full-backup-${backupStamp(at)}.zip`;
}
